import { get } from '@ember/helper';
import Component from '@glimmer/component';
import findBy from '@nullvoxpopuli/ember-composable-helpers/helpers/find-by';
import PowerSelect from 'ember-power-select/components/power-select';
import { isEmpty, or } from 'ember-truth-helpers';
import type { RecurringOption } from 'garaje/components/invites/recurring-invites-container';
import RecurringRule from 'garaje/models/recurring-rule';
import moment from 'moment-timezone';
import { localCopy } from 'tracked-toolbox';

interface InvitesRecurringInvitesSignature {
  Args: {
    expectedArrivalTime?: Date | null;
    onChange: (value: RecurringOption) => void;
    selected?: string;
    isReadonly?: boolean;
    disabled?: boolean;
  };
  Element: HTMLDivElement;
}

export default class RecurringInvites extends Component<InvitesRecurringInvitesSignature> {
  @localCopy('args.onChange', () => () => {}) onChange!: this['args']['onChange'];

  get rruleOptions(): RecurringOption[] {
    return this.updateRruleOptions(this.args.expectedArrivalTime);
  }

  get selected(): string {
    return this.args.selected || '';
  }

  weekOfMonth(date?: Date | string | null): number {
    return Math.ceil(moment(date).date() / 7);
  }

  isLastInstanceOfDayInMonth(date?: Date | string | null): boolean {
    // If next week is next month, then this is the last instance of a day this month
    return this.weekOfMonth(moment(date).add(1, 'week').toDate()) === 1;
  }

  updateRruleOptions(date?: Date | null): RecurringOption[] {
    const rruleOptions = [{ display: 'Does not repeat', value: '' }];
    const week = this.weekOfMonth(date);

    const daily = new RecurringRule({ frequency: 'DAILY' });
    const weekly = new RecurringRule({
      frequency: 'WEEKLY',
      byDay: moment(date).format('dd').toUpperCase(),
    });
    const monthly = new RecurringRule({
      frequency: 'MONTHLY',
      byDay: `${this.weekOfMonth(date)}${moment(date).format('dd').toUpperCase()}`,
    });
    const lastDay = new RecurringRule({
      frequency: 'MONTHLY',
      byDay: `-1${moment(date).format('dd').toUpperCase()}`,
    });
    const yearly = new RecurringRule({
      frequency: 'YEARLY',
      byMonth: moment(date).format('M'),
      byMonthDay: moment(date).format('D'),
    });
    const weekdays = new RecurringRule({ frequency: 'WEEKLY', byDay: 'MO,TU,WE,TH,FR' });

    const options = [
      { display: daily.ruleToText(), value: daily.toString() },
      { display: weekly.ruleToText(), value: weekly.toString() },
      ...(week <= 4 ? [{ display: monthly.ruleToText(), value: monthly.toString() }] : []),
      ...(this.isLastInstanceOfDayInMonth(date) ? [{ display: lastDay.ruleToText(), value: lastDay.toString() }] : []),
      { display: yearly.ruleToText(), value: yearly.toString() },
      { display: weekdays.ruleToText(), value: weekdays.toString() },
    ];

    return [...rruleOptions, ...options];
  }

  <template>
    <div data-test-rrule id="rrule-select" ...attributes>
      <PowerSelect
        @disabled={{or @isReadonly (isEmpty @expectedArrivalTime) @disabled}}
        @loadingMessage="Loading..."
        @onChange={{this.onChange}}
        @options={{this.rruleOptions}}
        @placeholder="Does not repeat"
        @searchEnabled={{false}}
        @selected={{findBy "value" this.selected this.rruleOptions}}
        as |option|
      >
        {{get option "display"}}
      </PowerSelect>
    </div>
  </template>
}
